import type { Stripe, StripeConstructorOptions } from "@stripe/stripe-js"
// @ts-ignore
import { loadStripe } from '@stripe/stripe-js/pure'
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { use, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { StripeIntent } from "@imago/model"
import { PaymentError, useConfig, useFinalizeStripe, Zoned } from "../api.ts"

declare const loadStripe: (
  publishableKey: string,
  options?: StripeConstructorOptions | undefined
) => Promise<Stripe | null>

function useColorScheme() {
	const darkPreferred = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)')
	const [colorScheme, setColorScheme] = useState(darkPreferred.matches ? 'dark' : 'light')
	useEffect(() => {
		darkPreferred.addEventListener('change', ev => {
			setColorScheme(ev.matches ? "dark" : "light")
		});
	}, [])
	return colorScheme
}

function useThemeColors() {
	useColorScheme() // ensure we re-render on every theme switch

	const style = getComputedStyle(document.body)
	return {
		primary: style.getPropertyValue('--md-sys-color-primary'),
		onSurface: style.getPropertyValue('--md-sys-color-on-surface'),
		onSurfaceVariant: style.getPropertyValue('--md-sys-color-on-surface-variant'),
		outline: style.getPropertyValue('--md-sys-color-outline'),
		outlineVariant: style.getPropertyValue('--md-sys-color-outline-variant'),
		surfaceContainerLowest: style.getPropertyValue('--md-sys-color-surface-container-lowest'),
		surfaceContainerLow: style.getPropertyValue('--md-sys-color-surface-container-low'),
		surfaceContainer: style.getPropertyValue('--md-sys-color-surface-container'),
		surfaceContainerHigh: style.getPropertyValue('--md-sys-color-surface-container-high'),
		surfaceContainerHighest: style.getPropertyValue('--md-sys-color-surface-container-highest'),
	}
}

let stripePromiseByKey: Map<string, Promise<Stripe | null>> = new Map()

export function StripeElements({intent: intentPromise, children}: {
	intent: Promise<StripeIntent | null>
	children?: any
}) {
	const intent = use(intentPromise)
	const colors = useThemeColors()

	const config = useConfig()
	const key = intent?.stripe_secret.publishableKey ?? config.stripePublishableKey
	if (!stripePromiseByKey.get(key)) {
		stripePromiseByKey.set(key, loadStripe(key))
	}
	const stripePromise = stripePromiseByKey.get(key)!

	return !intent ? <></> : <Elements stripe={stripePromise} options={{
		clientSecret: intent.stripe_secret.clientSecret,
		customerSessionClientSecret: intent.stripe_secret.customerSessionClientSecret,
		appearance: {
			theme: "flat",
			variables: {
				colorPrimary: colors.primary,
				colorText: colors.onSurface,
				borderRadius: '4px',
				focusBoxShadow: 'none',
				accordionItemSpacing: '0',
			},
			rules: {
				'.AccordionItem': {
					backgroundColor: 'transparent',
					paddingLeft: '16px',
					paddingRight: '16px',
					border: '0px solid transparent',
				},
				'.Input': {
					backgroundColor: colors.surfaceContainerHigh,
					borderRadius: '0',
					border: '0',
					borderBottom: `1px solid ${colors.onSurfaceVariant}`,
					marginBottom: '1px',
					transition: 'none',
				},
				'.Input:focus': {
					borderBottom: `2px solid ${colors.primary}`,
					marginBottom: '0px',
					transition: 'none',
				},
				'.CheckboxInput': {
					backgroundColor: 'transparent',
					border: `2px solid ${colors.onSurfaceVariant}`,
					borderRadius: '2px',
				},
				'.CheckboxInput--checked': {
					border: `2px solid ${colors.primary}`,
				},
				'.Block': {
					backgroundColor: 'transparent',
					boxShadow: 'none',
				},
				'.BlockDivider': {
					backgroundColor: 'transparent',
				},
				'.RedirectText': {
					borderTop: '0.75rem solid transparent',
				},
				'.PickerItem--selected, .PickerItem--selected:focus, .PickerItem--selected:hover': {
					backgroundColor: 'transparent',
					border: `1px solid ${colors.outlineVariant}`,
					boxShadow: 'none',
				},
				'.PickerItem--highlight, .PickerItem--highlight:focus, .PickerItem--highlight:hover': {
					backgroundColor: 'transparent',
					border: `1px solid ${colors.primary}`,
					boxShadow: `0 0 4px ${colors.primary}`,
				},
				'.PickerItem, .PickerItem:hover, .PickerItem:focus': {
					backgroundColor: 'transparent',
					border: `1px solid ${colors.outline}`,
					boxShadow: 'none',
				},
			},
		}
	}}>
		{children}
	</Elements>
}

export function StripePaymentElement() {
	const div = useRef<HTMLDivElement>(null)

	useLayoutEffect(() => {
		const pse = div.current!.getElementsByClassName('__PrivateStripeElement')[0] as HTMLDivElement
		const iframe = div.current!.getElementsByTagName('iframe')[0]!
		if (!pse || !iframe) return

		function pseOverride() {
			pse.style.height = ''
		}
		pseOverride()
		const pseObserver = new MutationObserver(pseOverride)
		pseObserver.observe(pse, {attributes: true, attributeFilter: ['style']})

		function iframeOverride() {
			iframe.style.transition = 'height 0.2s'

			// At the beginning, iframe has 2px height. No idea why, but this breaks transitions.
			if (iframe.style.height === '2px') {
				iframe.style.height = '313px'
			}
		}
		iframeOverride()
		const iframeObserver = new MutationObserver(iframeOverride)
		iframeObserver.observe(iframe, {attributes: true, attributeFilter: ['style']})

		return () => {
			pseObserver.disconnect()
			iframeObserver.disconnect()
		}
	}, [useElements()]) // the iframe will be there <=> elements !== null

	return <div ref={div}>
		<PaymentElement options={{
			layout: {
				type: 'accordion',
			}
		}}/>
	</div>
}

export function useConfirmPayment() {
	const stripe = useStripe() ?? undefined
	const finalizeStripe = useFinalizeStripe()
	const elements = useElements() ?? undefined

	const confirm = useMutation({
		mutationFn: async () => {
			const result = await stripe!.confirmPayment({
				elements: elements!,
				confirmParams: {
					return_url: location.href,
				},
				redirect: 'if_required',
			})
			if (result.error) {
				throw new PaymentError(result.error.message, {cause: result.error})
			}
			await finalizeStripe.mutateAsync({intent: result.paymentIntent.id})
		}
	})
	if (stripe === undefined || elements === undefined) return
	return confirm
}
